// The full-page checkout in steps (CheckoutPage, 25 Sept): which steps it
// has, what the progress says, the rider cards one at a time, what a finished
// rider reads like on Review, the resume entry, and the Paystack return's
// patience. Pure: no React, no network, no clock (every time is passed in).
// Pinned by e2e/checkout-steps-logic.spec.ts.
//
// A rider is checked HERE when the guest presses Next, and saved there and
// then with the one-rider writer (save_booking_participant_row; the state
// around those saves is state/rider-autosave.ts), so a closed tab loses at
// most the rider being typed. Still positional until the rider-ID work re-keys
// riders onto _pid. Nothing here stores an answer or the booker's contact
// anywhere: the resume entry is booking ids and line summaries only, and the
// riders already in come back from the server's reader.

import {
  describeQuestionProblem,
  displayOrder,
  formProblems,
  isEmptyAnswer,
  isFieldVisible,
  withoutSavedProblems,
  type GuestNoun,
  type QuestionField,
} from "@vertu/shared";
import {
  nounTitle,
  savedGroupKeys,
  savedRowKeys,
  typedRow,
  type CheckoutDraft,
  type CheckoutForm,
  type CheckoutVisit,
  type DraftRow,
} from "./booking-questions-form";

// ─── Steps ──────────────────────────────────────────────────────────────────

export type StepId = "details" | "riders" | "booking" | "review";
export const STEP_IDS: readonly StepId[] = ["details", "riders", "booking", "review"];

export type CheckoutStep = { id: StepId; label: string };

/**
 * The steps this checkout has, in order. A step with nothing in it is left
 * out: no per-person questions, no Riders step; no once-per-booking
 * questions, no Booking details step (Special requests then sits with Your
 * details, as it does in the drawer). Your details and Review & pay are
 * always there.
 */
export function checkoutSteps(input: { riders: boolean; bookingDetails: boolean; noun: GuestNoun }): CheckoutStep[] {
  const steps: CheckoutStep[] = [{ id: "details", label: "Your details" }];
  if (input.riders) steps.push({ id: "riders", label: ridersLabel(input.noun) });
  if (input.bookingDetails) steps.push({ id: "booking", label: "Booking details" });
  steps.push({ id: "review", label: "Review & pay" });
  return steps;
}

/** "Riders" from {one:"rider", other:"riders"}; "Guests" by default. */
export function ridersLabel(noun: GuestNoun): string {
  const other = noun.other.trim() || "guests";
  return other.charAt(0).toUpperCase() + other.slice(1);
}

/** The step, or the nearest one that exists (a saved step the cart no longer has). */
export function stepOrFirst(steps: readonly CheckoutStep[], id: StepId | null | undefined): StepId {
  if (id && steps.some((s) => s.id === id)) return id;
  if (id) {
    const wanted = STEP_IDS.indexOf(id);
    const later = steps.find((s) => STEP_IDS.indexOf(s.id) >= wanted);
    if (later) return later.id;
  }
  return steps[0]?.id ?? "details";
}

export function stepIndex(steps: readonly CheckoutStep[], id: StepId): number {
  return Math.max(0, steps.findIndex((s) => s.id === id));
}

export function nextStepOf(steps: readonly CheckoutStep[], id: StepId): StepId | null {
  return steps[stepIndex(steps, id) + 1]?.id ?? null;
}

export function prevStepOf(steps: readonly CheckoutStep[], id: StepId): StepId | null {
  const i = stepIndex(steps, id);
  return i > 0 ? steps[i - 1]!.id : null;
}

/** "Step 2 of 4 · Riders": the phone's progress line. */
export function stepProgressText(steps: readonly CheckoutStep[], id: StepId): string {
  const i = stepIndex(steps, id);
  return `Step ${i + 1} of ${steps.length} · ${steps[i]?.label ?? ""}`;
}

/**
 * "Step 2 of 4": the live announcement on a step change. The step heading
 * takes focus at the same moment and says its own name ("Riders"), so the
 * announcement adds only where the guest is, never the name a second time.
 */
export function stepPositionText(steps: readonly CheckoutStep[], id: StepId): string {
  return `Step ${stepIndex(steps, id) + 1} of ${steps.length}`;
}

// ─── Riders, one card at a time ─────────────────────────────────────────────

/** One rider card: a guest of one form, numbered across its visit ("Rider 3"). */
export type RiderSlot = {
  /** Stable within a page: booking id and position. */
  key: string;
  bookingId: string;
  /** 0-based position in the form's rows. */
  ordinal: number;
  /** 1-based, across the visit, as the drawer numbers them. */
  number: number;
  visitKey: string;
};

/** Every rider card of the checkout, in the order the forms list them. */
export function riderSlots(visits: readonly CheckoutVisit[]): RiderSlot[] {
  const slots: RiderSlot[] = [];
  for (const visit of visits)
    visit.forms.forEach((form, i) => {
      if (form.fields.length === 0) return;
      for (let ordinal = 0; ordinal < form.party_size; ordinal++)
        slots.push({
          key: `${form.booking_id}#${ordinal}`,
          bookingId: form.booking_id,
          ordinal,
          number: (visit.riderStart[i] ?? 0) + ordinal + 1,
          visitKey: visit.key,
        });
    });
  return slots;
}

/** The slot's form and visit, or null (the forms changed under it). */
export function slotForm(
  visits: readonly CheckoutVisit[],
  slot: RiderSlot,
): { visit: CheckoutVisit; form: CheckoutForm } | null {
  for (const visit of visits)
    for (const form of visit.forms) if (form.booking_id === slot.bookingId) return { visit, form };
  return null;
}

/**
 * The first thing one rider still has to answer, worded for the guest
 * ("Rider 2: enter age."), or null when the rider is complete. The same rules
 * as the whole form (formProblems), for this one row; an answer the server
 * keeps for a guest (a withheld private answer) counts as given.
 */
export function riderIssue(visits: readonly CheckoutVisit[], draft: CheckoutDraft, slot: RiderSlot): string | null {
  return riderProblem(visits, draft, slot)?.message ?? null;
}

/**
 * riderIssue with the question it is about (its key), so the page can take
 * the guest to it. The first one in the order the card SHOWS the questions
 * (displayOrder: an empty Height in the age row before a guardian's name
 * below the row), so the words and the focus land on the top-most gap.
 */
export function riderProblem(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  slot: RiderSlot,
): { key: string; message: string } | null {
  const found = slotForm(visits, slot);
  if (!found) return null;
  const { visit, form } = found;
  const row = typedRow(form.fields, draft.rows[form.booking_id]?.[slot.ordinal]);
  const saved = savedRowKeys(form, draft)[slot.ordinal];
  const problem = withoutSavedProblems(formProblems(displayOrder(form.fields), [row], 1), [saved])[0];
  if (!problem) return null;
  const field = form.fields.find((f) => f.key === problem.key);
  return { key: problem.key, message: describeQuestionProblem(problem, field, visit.noun, slot.number - 1) };
}

/** The first once-per-booking question of a visit still missing (or with a wrong) answer: its key, or null. */
export function groupProblemKey(visit: CheckoutVisit, draft: CheckoutDraft): string | null {
  const g = visit.groupForm;
  if (!g) return null;
  const answers = typedRow(g.group_fields, draft.group[visit.key]);
  const problem = withoutSavedProblems(formProblems(g.group_fields, [answers], 1), [savedGroupKeys(g, draft)])[0];
  return problem?.key || null;
}

/**
 * The element that holds one question's answer, by the id QuestionControl
 * gives it (`<prefix>-<key>`), or its first option (`<prefix>-<key>-…`: a
 * yes/no or a list of choices). Never a longer key that merely starts the
 * same (medical vs medical_details): the option ids go on after a "-".
 */
export function answerElementId(prefix: string, key: string): { exact: string; option: string } {
  return { exact: `${prefix}-${key}`, option: `${prefix}-${key}-` };
}

/** Is this rider's card complete (nothing left to answer)? */
export function riderComplete(visits: readonly CheckoutVisit[], draft: CheckoutDraft, slot: RiderSlot): boolean {
  return riderIssue(visits, draft, slot) === null;
}

/** The first rider still missing something, or -1. */
export function firstIncompleteRider(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  slots: readonly RiderSlot[],
): number {
  return slots.findIndex((slot) => !riderComplete(visits, draft, slot));
}

/** A chip's state: done (a tick), current (a dot), or not done (a ring). */
export type RiderChipState = "done" | "current" | "todo";

export function riderChipState(complete: boolean, current: boolean): RiderChipState {
  return current ? "current" : complete ? "done" : "todo";
}

/**
 * The chip's words for a screen reader: "Rider 2, complete" (the drawer's
 * pager ticks a complete rider) or, with done "saved", "Rider 2, saved" /
 * "Rider 2, not saved yet" (the full page ticks a rider once it is saved).
 */
export function riderChipLabel(
  noun: GuestNoun,
  number: number,
  state: RiderChipState,
  done: "complete" | "saved" = "complete",
): string {
  const what =
    state === "done" ? done : state === "current" ? "showing now" : done === "saved" ? "not saved yet" : "not complete yet";
  return `${nounTitle(noun)} ${number}, ${what}`;
}

// ─── Saving in parts ────────────────────────────────────────────────────────

/**
 * What one save covers. "all" is the drawer's Proceed (riders, then the
 * once-per-booking answers, then — on Proceed — the statements); "rows" the
 * riders alone; "group" the end of Booking details; "rest" everything but
 * the riders (the full page's Pay: its riders were saved one at a time,
 * state/rider-autosave.ts). A scoped save checks and writes only its own
 * part: the visits it is handed have the other parts taken out, so
 * commitPlan and visitIssue need no change.
 */
export type SaveScope = "all" | "rows" | "group" | "rest";

export function scopeVisits<V extends CheckoutVisit>(visits: readonly V[], scope: SaveScope): V[] {
  if (scope === "all") return visits as V[];
  if (scope === "rows") return visits.map((v) => ({ ...v, groupForm: null, confirmations: [] }));
  if (scope === "rest") return visits.map((v) => ({ ...v, forms: [], riderStart: [] }));
  return visits.map((v) => ({ ...v, forms: [], riderStart: [], confirmations: [] }));
}

// ─── Review: each rider on one line ─────────────────────────────────────────

const FIRST_KEYS = ["first_name"];
const LAST_KEYS = ["surname", "last_name"];
const FULL_KEYS = ["name", "full_name"];

function text(v: unknown): string {
  return typeof v === "string" ? v.trim().replace(/\s+/g, " ") : "";
}

/** The rider's name as typed (a marked name question first, then the preset keys), or "". */
export function riderName(fields: readonly QuestionField[], row: DraftRow | undefined): string {
  if (!row) return "";
  const byProfile = (slot: string) => fields.find((f) => f.profile === slot && f.sensitive !== true)?.key;
  const pick = (keys: readonly string[], slot: string) => {
    const marked = byProfile(slot);
    if (marked) return text(row[marked]);
    for (const key of keys) {
      const field = fields.find((f) => f.key === key && f.sensitive !== true);
      if (field) return text(row[key]);
    }
    return "";
  };
  const full = pick(FULL_KEYS, "full_name");
  const first = pick(FIRST_KEYS, "first_name");
  const last = pick(LAST_KEYS, "last_name");
  return [first, last].filter(Boolean).join(" ") || full;
}

/** How a rider reads on Review & pay. */
export type RiderReview = {
  /** "Rider 2" */
  title: string;
  /** What was typed for the name, or "" (never a private answer). */
  name: string;
  complete: boolean;
  /**
   * A private (sensitive) question is answered, in the draft or kept by the
   * server: Review says "provided, kept private" and never shows the answer.
   */
  privateProvided: boolean;
};

export const PRIVATE_PROVIDED = "Private answers: provided, kept private";

export function riderReview(visits: readonly CheckoutVisit[], draft: CheckoutDraft, slot: RiderSlot): RiderReview {
  const found = slotForm(visits, slot);
  const noun = found?.visit.noun ?? { one: "guest", other: "guests" };
  const title = `${nounTitle(noun)} ${slot.number}`;
  if (!found) return { title, name: "", complete: false, privateProvided: false };
  const { form } = found;
  const raw = draft.rows[form.booking_id]?.[slot.ordinal];
  const row = typedRow(form.fields, raw);
  const saved = savedRowKeys(form, draft)[slot.ordinal];
  const privateProvided = form.fields.some(
    (f) =>
      f.sensitive === true &&
      f.retired !== true &&
      isFieldVisible(f, row) &&
      (!isEmptyAnswer(f, row[f.key]) || saved?.has(f.key) === true),
  );
  return {
    title,
    name: riderName(form.fields, raw),
    complete: riderComplete(visits, draft, slot),
    privateProvided,
  };
}

// ─── Resume after a reload or the Paystack round trip ───────────────────────

/**
 * sessionStorage['vt-checkout:<tenant slug>']: the cart token, the booking
 * ids, the lines as the cart shows them (service, option, start, party,
 * price), where the guest was, and a few yes/no facts (a code applied and
 * its kind, the marketing tick, whether Special requests or a Country were
 * given). NEVER an answer and NEVER the booker's contact: those stay in memory (answers the guest saved come back from the
 * server's readers; the booker's details from the guest profile as always).
 * sessionStorage survives a reload and the Paystack round trip in the same
 * tab and does not linger on a shared device.
 */
export function resumeKey(tenantSlug: string): string {
  return `vt-checkout:${tenantSlug}`;
}

export const RESUME_VERSION = 1;
/** An entry older than this is not offered (a hold never lives this long). */
export const RESUME_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/**
 * The line metadata a resumed line keeps: identifiers, times, prices, and
 * what the cart's own rules read (where the line came from, a combo's or a
 * special date's lock, a room's guest cap, a deposit). Plain values the
 * widget itself stamped when the line was added; nothing typed by a guest.
 * A line with any other kind of value (a list of add-ons, a special date's
 * menu, a co-hosted pairing) is not resumed at all: see buildResume.
 */
export const RESUME_METADATA_KEYS: readonly string[] = [
  "booking_id",
  "booking_reference",
  // Where the line came from: "combo" and "event" lines are locked against
  // in-cart edits ("chidesk" lines are never resumed).
  "source",
  "starts_at",
  "ends_at",
  "party_size",
  "party_size_min",
  "visit_guests",
  "unit_capacity",
  "pricing_basis",
  "amount_cents",
  "unit_price_cents",
  "addons_cents",
  "deposit_cents",
  "requires_full_payment",
  "combo_id",
  "combo_name",
  "combo_auto",
  "option_led",
  "session_minutes",
  "tenant_tz",
  "tz_offset_minutes",
  "seat_label",
  "seat_element_id",
  "floor_plan_id",
  "floor_plan_name",
  "seating_area_id",
  "seating_area_name",
  "general_reservation",
  "special_date_event_id",
  "special_date_event_name",
  "event_id",
  "ticket_type_id",
  "venue_location",
  "selections_summary",
];

/**
 * The last trip to Paystack from this checkout: when it began, the payment's
 * reference (a payment id, not personal data), and whether Paystack has
 * since sent the browser back to the page with it.
 *   notPaid     Paystack said this trip was not paid and the checkout
 *               reopened on Review: a later page load asks again, but
 *               quietly (nothing opens by itself unless the money moved,
 *               and a checkout whose hold has ended is simply forgotten).
 *   handedBack  the trip's page was an earlier page of this same checkout
 *               that the second-charge guard handed back (resume). Once
 *               Paystack says that page was not paid either, the next Pay
 *               asks for a new page (freshPage): a page the guest cancelled
 *               may not take a payment, and handing it back again would
 *               loop.
 * Both only ever appear as true.
 */
export type ResumePayment = {
  startedAt: number;
  reference: string;
  returned: boolean;
  notPaid?: true;
  handedBack?: true;
};

/** Which kinds of code were applied (never the code): a voucher is a promo code. */
export type CodeKind = "promo" | "gift_card";

/**
 * Whether the guest had typed Special requests, or chosen a Country other
 * than the default, when the entry was written: yes or no, never what. A
 * reopened checkout fills both back in from the server's copy (the unpaid
 * payment, the held bookings); when that copy could not be read, the notice
 * asks for them again instead of silently dropping them.
 */
export type ResumeGiven = { notes: boolean; country: boolean };

export type ResumeLine = {
  serviceId: string;
  serviceName: string;
  serviceType: string;
  unitId: string | null;
  unitName: string | null;
  description: string;
  quantity: number;
  unitPrice: number | null;
  currency: string;
  brandId: string | null;
  brandName: string | null;
  metadata: Record<string, string | number | boolean | null>;
};

export type CheckoutResume = {
  v: typeof RESUME_VERSION;
  cartToken: string;
  bookingIds: string[];
  lines: ResumeLine[];
  step: StepId;
  riderIndex: number;
  savedAt: number;
  /**
   * The last trip to Paystack (null: the guest never got that far). A page
   * that loads with this set asks the server about that payment before
   * anything else: a confirmed one shows the confirmation; after Paystack
   * sent the browser back (returned) it is "Confirming your payment…" again;
   * only after the browser's own Back (not returned) and a "not paid" from
   * the server does Review & pay reopen (resumeNotice).
   */
  payment: ResumePayment | null;
  /**
   * A promo code or gift card was applied when the entry was written. Never
   * the code (a gift-card code is a bearer secret): the restored Review asks
   * for it again (CODES_AGAIN_MESSAGE) instead of silently showing the full
   * price.
   */
  codesApplied: boolean;
  /** Which kinds were applied (never the code), so the notice names the right one. */
  codeKinds: CodeKind[];
  /**
   * The one code applied was the booking link's own promo code (link-promo.ts;
   * it is in the page's address already, so this keeps nothing secret). Only
   * then does a reopened checkout count the codes as back on by themselves:
   * the link's code re-applies itself, a code the guest typed does not.
   */
  linkCodeOnly: boolean;
  /**
   * The optional marketing tick as the guest left it (a yes or no, never an
   * answer); null: not known. A reopened checkout shows it the same way, so
   * a second Pay never records a "no" the guest did not give.
   */
  marketing: boolean | null;
  given: ResumeGiven;
};

type LineLike = {
  serviceId: string;
  serviceName: string;
  serviceType: string;
  unitId: string | null;
  unitName: string | null;
  description: string;
  quantity: number;
  unitPrice: number | null;
  currency: string;
  brandId: string | null;
  brandName: string | null;
  metadata: Record<string, unknown>;
};

function primitive(v: unknown): v is string | number | boolean | null {
  return v === null || typeof v === "string" || typeof v === "boolean" || (typeof v === "number" && Number.isFinite(v));
}

/** A value the entry cannot carry back faithfully: a non-empty list or an object. */
function structured(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return v !== null && typeof v === "object";
}

/**
 * The entry for this cart, or null when it cannot be resumed faithfully:
 * a spa treatment (it has no server row before payment, so nothing can be
 * re-checked), a line with no hold, a co-hosted pairing (its zeroed sibling
 * lives in metadata this entry does not keep), or a line carrying a list or
 * an object (add-ons, a special date's menu): the cart's edits read those,
 * and a line without them would edit as if there were none.
 */
export function buildResume(input: {
  cartToken: string;
  items: readonly LineLike[];
  step: StepId;
  riderIndex: number;
  now: number;
  payment?: ResumePayment | null;
  codesApplied?: boolean;
  codeKinds?: readonly CodeKind[];
  linkCodeOnly?: boolean;
  marketing?: boolean | null;
  given?: Partial<ResumeGiven>;
}): CheckoutResume | null {
  if (!input.cartToken || input.items.length === 0) return null;
  const lines: ResumeLine[] = [];
  const bookingIds: string[] = [];
  for (const item of input.items) {
    const md = item.metadata ?? {};
    if (md.source === "chidesk" || md.cohost != null) return null;
    if (Object.values(md).some(structured)) return null;
    const id = md.booking_id;
    if (typeof id !== "string" || !id) return null;
    const metadata: ResumeLine["metadata"] = {};
    for (const key of RESUME_METADATA_KEYS) if (key in md && primitive(md[key])) metadata[key] = md[key] as never;
    bookingIds.push(id);
    lines.push({
      serviceId: item.serviceId,
      serviceName: item.serviceName,
      serviceType: item.serviceType,
      unitId: item.unitId,
      unitName: item.unitName,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      currency: item.currency,
      brandId: item.brandId,
      brandName: item.brandName,
      metadata,
    });
  }
  return {
    v: RESUME_VERSION,
    cartToken: input.cartToken,
    bookingIds,
    lines,
    step: input.step,
    riderIndex: Math.max(0, Math.floor(input.riderIndex) || 0),
    savedAt: input.now,
    payment: input.payment ? { ...input.payment } : null,
    codesApplied: input.codesApplied === true,
    codeKinds: readCodeKinds(input.codeKinds),
    linkCodeOnly: input.codesApplied === true && input.linkCodeOnly === true,
    marketing: typeof input.marketing === "boolean" ? input.marketing : null,
    given: { notes: input.given?.notes === true, country: input.given?.country === true },
  };
}

/** The kinds of code, each once, in a fixed order (anything else dropped). */
function readCodeKinds(v: unknown): CodeKind[] {
  if (!Array.isArray(v)) return [];
  return (["promo", "gift_card"] as const).filter((kind) => v.includes(kind));
}

const str = (v: unknown): v is string => typeof v === "string";
const strOrNull = (v: unknown): v is string | null => v === null || typeof v === "string";

function readLine(v: unknown): ResumeLine | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const l = v as Record<string, unknown>;
  if (!str(l.serviceId) || !str(l.serviceName) || !str(l.serviceType) || !str(l.description) || !str(l.currency))
    return null;
  if (!strOrNull(l.unitId) || !strOrNull(l.unitName) || !strOrNull(l.brandId) || !strOrNull(l.brandName)) return null;
  if (typeof l.quantity !== "number" || !Number.isFinite(l.quantity) || l.quantity < 1) return null;
  if (!(l.unitPrice === null || (typeof l.unitPrice === "number" && Number.isFinite(l.unitPrice)))) return null;
  const md = l.metadata;
  if (!md || typeof md !== "object" || Array.isArray(md)) return null;
  // Only the kept keys, and only plain values: an entry from an older page,
  // or one edited by hand, never carries anything else back into the cart.
  const metadata: ResumeLine["metadata"] = {};
  for (const key of RESUME_METADATA_KEYS) {
    const value = (md as Record<string, unknown>)[key];
    if (key in md && primitive(value)) metadata[key] = value;
  }
  // A spa line is never resumed (buildResume keeps none): one here was not
  // written by this page.
  if (metadata.source === "chidesk") return null;
  if (!str(metadata.booking_id)) return null;
  return {
    serviceId: l.serviceId,
    serviceName: l.serviceName,
    serviceType: l.serviceType,
    unitId: l.unitId,
    unitName: l.unitName,
    description: l.description,
    quantity: Math.floor(l.quantity),
    unitPrice: l.unitPrice as number | null,
    currency: l.currency,
    brandId: l.brandId,
    brandName: l.brandName,
    metadata,
  };
}

/** A stored entry, checked strictly, or null (absent, unreadable, too old, or not this shape). */
export function parseResume(raw: string | null | undefined, now: number): CheckoutResume | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const e = v as Record<string, unknown>;
  if (e.v !== RESUME_VERSION || !str(e.cartToken) || !e.cartToken) return null;
  if (typeof e.savedAt !== "number" || !Number.isFinite(e.savedAt)) return null;
  if (now - e.savedAt > RESUME_MAX_AGE_MS || e.savedAt - now > 60_000) return null;
  if (!STEP_IDS.includes(e.step as StepId)) return null;
  const bookingIds = e.bookingIds;
  if (!Array.isArray(bookingIds) || bookingIds.length === 0 || !bookingIds.every((id) => str(id) && id !== ""))
    return null;
  if (!Array.isArray(e.lines) || e.lines.length === 0) return null;
  const lines = e.lines.map(readLine);
  if (lines.some((l) => l === null)) return null;
  const ids = (lines as ResumeLine[]).map((l) => l.metadata.booking_id as string);
  if (ids.length !== bookingIds.length || ids.some((id, i) => id !== bookingIds[i])) return null;
  const riderIndex = typeof e.riderIndex === "number" && Number.isFinite(e.riderIndex) ? Math.max(0, Math.floor(e.riderIndex)) : 0;
  return {
    v: RESUME_VERSION,
    cartToken: e.cartToken,
    bookingIds: bookingIds as string[],
    lines: lines as ResumeLine[],
    step: e.step as StepId,
    riderIndex,
    savedAt: e.savedAt,
    payment: readPayment(e.payment),
    codesApplied: e.codesApplied === true,
    codeKinds: readCodeKinds(e.codeKinds),
    // An entry from before this field: never counted as the link's own.
    linkCodeOnly: e.codesApplied === true && e.linkCodeOnly === true,
    marketing: typeof e.marketing === "boolean" ? e.marketing : null,
    given: readGiven(e.given),
  };
}

function readGiven(v: unknown): ResumeGiven {
  const g = v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  return { notes: g.notes === true, country: g.country === true };
}

/** The stored trip to Paystack, checked strictly, or null. */
function readPayment(v: unknown): ResumePayment | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const p = v as Record<string, unknown>;
  if (typeof p.startedAt !== "number" || !Number.isFinite(p.startedAt)) return null;
  if (!str(p.reference) || p.reference.length === 0 || p.reference.length > 200) return null;
  return {
    startedAt: p.startedAt,
    reference: p.reference,
    returned: p.returned === true,
    ...(p.notPaid === true ? { notPaid: true as const } : {}),
    ...(p.handedBack === true ? { handedBack: true as const } : {}),
  };
}

/**
 * The entry after Paystack sent the browser back to the page with this
 * payment's reference: from now on a reload asks about that payment again
 * ("Confirming your payment…") and never reopens Pay by itself, because
 * Paystack's return is also what a paid guest sees before the webhook lands.
 */
export function markReturned(entry: CheckoutResume, reference: string, now: number): CheckoutResume {
  // The same trip keeps what is known about it (notPaid, handedBack).
  const same = entry.payment?.reference === reference ? entry.payment : null;
  return {
    ...entry,
    payment: { ...same, startedAt: entry.payment?.startedAt ?? now, reference, returned: true },
  };
}

/** The entry once Paystack said its trip was not paid and the checkout reopened (another trip: unchanged). */
export function markNotPaid(entry: CheckoutResume, reference: string): CheckoutResume {
  if (!entry.payment || entry.payment.reference !== reference) return entry;
  return { ...entry, payment: { ...entry.payment, notPaid: true } };
}

// ─── The Paystack return: never silent ──────────────────────────────────────

/**
 * How long the return waits for the webhook to confirm the payment, and when
 * it asks again (ms after the first ask). About 20 seconds in all; then the
 * page says it is still confirming and where the confirmation will arrive.
 * Since the return asks Paystack first (state/payment-return.ts), this wait
 * is only for a payment Paystack gave no answer about; "Payment received"
 * goes on waiting longer (PAID_POLL_AT_MS).
 */
export const CONFIRM_POLL_AT_MS: readonly number[] = [0, 1500, 3000, 5000, 7500, 10_000, 13_000, 16_500, 20_000];

/** Is a refusal worth asking again? Only while the payment may still be settling. */
export function confirmRetryable(reason: string): boolean {
  return reason !== "not_a_booking";
}

export const CONFIRMING_MESSAGE = "Confirming your payment…";
export const STILL_CONFIRMING_MESSAGE =
  "We're still confirming your payment. If it went through, your confirmation will arrive by email or WhatsApp shortly — please check there before booking again.";
/**
 * Review & pay reopened after a trip to Paystack the server has no payment
 * for. Never "not completed" as a fact: the server only knows it has not
 * received one (a late webhook reads the same), so after Paystack's own
 * return the guest is told where a real payment's confirmation would arrive.
 */
export const NOT_RECEIVED_MESSAGE = "We haven't received your payment. Your details are saved.";
export const NOT_RECEIVED_AFTER_RETURN_MESSAGE =
  "We haven't received your payment yet. If you did pay, your confirmation will arrive by email or WhatsApp — please check there before paying again. Your details are saved.";
/** Promo codes and gift cards are never kept (a gift-card code is a bearer secret). */
export const CODES_AGAIN_MESSAGE = "Apply your promo code or gift card again before you pay.";

/**
 * What a checkout reopened after its trip to Paystack fills back in. Special
 * requests and Country come from the server's copy (the unpaid payment's
 * notes, the held bookings' country: paystack-payment-status, with the cart
 * token as proof), never from this tab's storage; the marketing tick from
 * the entry (a yes or no). null: not read, so the notice asks for it again
 * when the guest had given one (ResumeGiven).
 *   freshPage  the next Pay asks for a new Paystack page (the trip's page was
 *              already handed back once and Paystack said it was not paid).
 *              Only on Paystack's "not paid" (`notPaid`): on Back to your
 *              checkout Paystack said nothing, and that page may still be
 *              the guest's own, going through, so the guard may hand it back.
 */
export type ReopenedFields = {
  notes: string | null;
  country: string | null;
  marketing: boolean | null;
  freshPage: boolean;
};

/** The unpaid payment's own copy of what the guest typed (paystack-payment-status). */
export type CheckoutDetails = { notes: string | null; country: string | null };

/** What the reopened checkout fills in: the server's copy, the entry's tick, and whether to ask for a new page. */
export function reopenedFields(
  entry: { payment: ResumePayment | null; marketing: boolean | null },
  details: CheckoutDetails | null,
  { notPaid = false }: { notPaid?: boolean } = {},
): ReopenedFields {
  return {
    notes: details?.notes ?? null,
    country: details?.country ?? null,
    marketing: entry.marketing,
    freshPage: notPaid && entry.payment?.handedBack === true,
  };
}

/**
 * What the guest must do again on a checkout reopened after its trip to
 * Paystack, only what applies, as one sentence: the terms tick always (it is
 * never kept), a promo code or gift card that was applied (never kept), and
 * Special requests or a Country the server's copy could not bring back.
 * "Tick the terms again and re-apply your promo code before paying."
 */
export function redoMessage(
  entry: { codesApplied: boolean; codeKinds?: readonly CodeKind[]; given?: ResumeGiven },
  fields: Pick<ReopenedFields, "notes" | "country"> | null = null,
): string {
  const todo = ["tick the terms again"];
  const kinds = entry.codeKinds ?? [];
  if (kinds.length === 2) todo.push("re-apply your promo code and gift card");
  else if (kinds[0] === "promo") todo.push("re-apply your promo code");
  else if (kinds[0] === "gift_card") todo.push("re-apply your gift card");
  else if (entry.codesApplied) todo.push("re-apply your promo code or gift card");
  if (entry.given?.notes && !fields?.notes) todo.push("add your special requests again");
  if (entry.given?.country && !fields?.country) todo.push("choose your country again");
  const list = todo.length === 1 ? todo[0]! : `${todo.slice(0, -1).join(", ")} and ${todo[todo.length - 1]}`;
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} before paying.`;
}
/** The kept checkout, offered on "We're still confirming…". */
export const BACK_TO_CHECKOUT_HINT = "Didn't finish paying? Your checkout is kept for you.";
export const BACK_TO_CHECKOUT_LAPSED_MESSAGE =
  "We couldn't reopen your checkout: its hold has ended. Please check availability again.";
/**
 * The kept checkout is not the cart the server (or this tab) holds now: a
 * line was added, removed or let go after the guest left it. Never "its hold
 * has ended": the hold may be fine.
 */
export const BACK_TO_CHECKOUT_CHANGED_MESSAGE =
  "We couldn't reopen your checkout: your booking changed after you left it. Please choose your times again.";

/**
 * The notice a restored checkout shows (null: none): after a trip to
 * Paystack, that the payment has not been received (never that it failed),
 * and what to do again before paying (redoMessage); a plain reload, only a
 * code to apply again.
 */
export function resumeNotice(
  entry: { payment: ResumePayment | null; codesApplied: boolean; codeKinds?: readonly CodeKind[]; given?: ResumeGiven },
  fields: Pick<ReopenedFields, "notes" | "country"> | null = null,
): string | null {
  if (!entry.payment) return entry.codesApplied ? CODES_AGAIN_MESSAGE : null;
  const head = entry.payment.returned ? NOT_RECEIVED_AFTER_RETURN_MESSAGE : NOT_RECEIVED_MESSAGE;
  return `${head} ${redoMessage(entry, fields)}`;
}

/**
 * The codes a reopened checkout asked for again are back on by themselves:
 * the one code it had was the booking link's own (linkCodeOnly), and that
 * code is applied again now (a link's code re-applies itself as the checkout
 * opens: link-promo.ts). A code the guest typed, a second code, or a gift
 * card is never counted: the ask stays until the guest is told about it.
 */
export function codesBackOn(
  asked: { codesApplied: boolean; linkCodeOnly?: boolean },
  appliedNow: readonly { code: string; kind: string }[],
  linkCode: string | null,
): boolean {
  if (!asked.codesApplied) return true;
  if (asked.linkCodeOnly !== true || !linkCode) return false;
  return appliedNow.some((c) => c.kind !== "gift_card" && c.code === linkCode);
}

/** Said in place of "apply your code again" once the link's code is back on. */
export function linkCodeBackMessage(code: string): string {
  return `Your promo code ${code} from your link is applied again.`;
}

/** The same entry with nothing to re-apply: the notice once its codes are back on. */
export function withoutCodes<T extends { codesApplied: boolean; codeKinds?: readonly CodeKind[] }>(entry: T): T {
  return { ...entry, codesApplied: false, codeKinds: [] };
}

/**
 * What a reopened checkout's notice says now. While it is the notice the
 * checkout reopened with (another notice may have replaced it) and asked for
 * the link's code, once that code is back on it is the same notice without
 * the ask, or, when that left nothing to say (a plain reload), that the code
 * is applied again. Never empty: the drawer focuses the notice as it opens,
 * and its role="status" says the change.
 */
export function noticeNow(
  shown: string | null,
  resumed: {
    notice: string | null;
    noticeOnceCodesBack?: string | null;
    codesAsked?: { codesApplied: boolean; linkCodeOnly?: boolean };
  } | null,
  appliedNow: readonly { code: string; kind: string }[],
  linkCode: string | null,
): string | null {
  if (!resumed || shown === null || shown !== resumed.notice || !resumed.codesAsked) return shown;
  if (resumed.noticeOnceCodesBack === undefined || !resumed.codesAsked.codesApplied) return shown;
  if (!codesBackOn(resumed.codesAsked, appliedNow, linkCode)) return shown;
  return resumed.noticeOnceCodesBack ?? linkCodeBackMessage(linkCode!);
}

// ─── The summary ────────────────────────────────────────────────────────────

/** An amount as the checkout's buttons show it ("ZAR 3,800"). */
export function formatDue(currency: string, cents: number): string {
  const c = Math.max(0, Math.round(cents));
  // Whole rands as before; cents always as two digits (a 25% code on R950
  // leaves R712.50, never "R712.5").
  const digits = c % 100 === 0 ? 0 : 2;
  return `${currency} ${(c / 100).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`;
}

/**
 * The summary's discount rows (Subtotal, "Discount · LAUNCH25", then Due now):
 * only when a code or gift card takes something off a total the codes apply
 * to — never a mixed cart, whose payment ignores codes. Promo codes are named;
 * a gift card is "gift card", never its code (a bearer secret).
 */
export function summaryDiscount(input: {
  currency: string;
  totalCents: number;
  discountCents: number;
  codes: readonly { code: string; kind: string }[];
  mixedCart: boolean;
}): { subtotal: string; label: string; amount: string } | null {
  if (input.mixedCart || input.discountCents <= 0 || input.totalCents <= 0) return null;
  const named = input.codes.filter((c) => c.kind !== "gift_card").map((c) => c.code);
  if (input.codes.some((c) => c.kind === "gift_card")) named.push("gift card");
  return {
    subtotal: formatDue(input.currency, input.totalCents),
    label: named.length > 0 ? `Discount · ${named.join(", ")}` : "Discount",
    amount: formatDue(input.currency, Math.min(input.discountCents, input.totalCents)),
  };
}

/**
 * The phone's one-line summary: "ZAR 3,800 · 4 riders · Sat 08:30". The
 * riders part only when the checkout has guest cards; the start only when
 * there is one.
 */
export function summaryBarText(input: {
  due: string;
  riders: number;
  noun: GuestNoun;
  when: string | null;
}): string {
  const parts = [input.due];
  if (input.riders > 0) parts.push(`${input.riders} ${input.riders === 1 ? input.noun.one : input.noun.other}`);
  if (input.when) parts.push(input.when);
  return parts.join(" · ");
}

/** The part of a get_booking_confirmation_by_payment_reference answer the poll reads. */
export type PaystackReturnAnswer = { ok: true } | { ok: false; reason: string };

/**
 * The return from Paystack, never silent (pay-now spec §7.6). Paystack sends
 * the browser back before, or as, it tells the server, so the first answer
 * is often "not paid yet": ask again on `schedule` (ms after the first ask,
 * about 20 seconds in all) until the payment is confirmed. `onWaiting` is
 * called once, on the first answer that is not a confirmation, so the page
 * can say "Confirming your payment…" only when there is something to wait
 * for. A payment that is not a booking (a gift card: not_a_booking) stops
 * at once; its own flow owns it. So does a refund (payment_refunded,
 * 20261001160010: the booking could not be kept, or another payment paid for
 * it): nothing more will confirm it. "Received, not confirmed" is asked
 * again (the webhook is still deciding). The last answer is returned either
 * way.
 */
export async function pollPaystackConfirmation<A extends PaystackReturnAnswer>(
  reference: string,
  options: {
    schedule: readonly number[];
    cancelled: () => boolean;
    onWaiting?: () => void;
    fetch: (reference: string) => Promise<A>;
    wait?: (ms: number) => Promise<void>;
    now?: () => number;
  },
): Promise<A | { ok: false; reason: "cancelled" }> {
  const wait = options.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = options.now ?? (() => Date.now());
  const start = now();
  let last: A | null = null;
  let waiting = false;
  for (const at of options.schedule) {
    const delay = start + at - now();
    if (delay > 0) await wait(delay);
    if (options.cancelled()) break;
    last = await options.fetch(reference);
    if (last.ok || last.reason === "not_a_booking" || last.reason === "payment_refunded") return last;
    if (!waiting) {
      waiting = true;
      options.onWaiting?.();
    }
  }
  return last ?? { ok: false, reason: "cancelled" };
}
